import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { kvGet, kvSet, queueAll, queuePut, queueDel } from "@/lib/offlineDb";
import { getDeviceId, newUid } from "@/lib/deviceId";

// true if the value still contains an unresolved local temp id
function hasTmp(v) {
  if (typeof v === "string") return v.startsWith("tmp-");
  if (Array.isArray(v)) return v.some(hasTmp);
  if (v && typeof v === "object") return Object.values(v).some(hasTmp);
  return false;
}

const OfflineCtx = createContext(null);
export const useOffline = () => useContext(OfflineCtx);

// Replace local temporary ids (tmp-*) with the real server ids captured during sync.
function resolveVal(v, map) {
  if (typeof v === "string") return map[v] || v;
  if (Array.isArray(v)) return v.map((x) => resolveVal(x, map));
  if (v && typeof v === "object") { const o = {}; for (const k in v) o[k] = resolveVal(v[k], map); return o; }
  return v;
}

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [bootstrap, setBootstrap] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [queue, setQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const deviceId = getDeviceId();
  const flushing = useRef(false);

  const reloadQueue = useCallback(async () => {
    const all = await queueAll();
    all.sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
    setQueue(all);
  }, []);

  useEffect(() => {
    (async () => {
      setBootstrap((await kvGet("bootstrap")) || null);
      setLastSync((await kvGet("lastSync")) || null);
      await reloadQueue();
    })();
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [reloadQueue]);

  const refreshBootstrap = useCallback(async (activityId, locationId) => {
    if (!activityId || !locationId || !navigator.onLine) return null;
    const { data } = await api.get(`/pos/offline-bootstrap?activity_id=${activityId}&location_id=${locationId}`);
    await kvSet("bootstrap", data);
    setBootstrap(data);
    const t = new Date().toISOString();
    await kvSet("lastSync", t);
    setLastSync(t);
    return data;
  }, []);

  const enqueue = useCallback(async (op) => {
    const full = { attempts: 0, status: "pending", created_at: new Date().toISOString(), device_id: deviceId, ...op };
    await queuePut(full);
    await reloadQueue();
    return full;
  }, [deviceId, reloadQueue]);

  const pendingBaseQty = useCallback((productId) => {
    let q = 0;
    for (const op of queue) {
      if (op.type === "sale" && op.status !== "synced") {
        for (const l of op.localSale?.lines || []) if (l.product_id === productId) q += l.base_qty || 0;
      }
    }
    return q;
  }, [queue]);

  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    setSyncing(true);
    try {
      const all = await queueAll();
      let map = (await kvGet("idMap")) || {};
      const todo = all.filter((o) => o.status === "pending").sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
      const done = new Set(all.filter((o) => o.status === "synced").map((o) => o.uid));
      const failed = new Set(all.filter((o) => o.status === "error").map((o) => o.uid));
      for (const op of todo) {
        // dependency handling
        if (Array.isArray(op.depends_on)) {
          if (op.depends_on.some((d) => failed.has(d))) {
            op.status = "error"; op.error = "Dépendance en échec — corrigez l'opération liée"; await queuePut(op); continue;
          }
          if (op.depends_on.some((d) => !done.has(d))) continue; // dependency not synced yet
        }
        try {
          const method = op.type === "cash_close" ? "post" : (op.method || "post");
          const sid = op.type === "cash_close" ? (map[op.session_id] || op.session_id) : null;
          if (op.type === "cash_close" && typeof sid === "string" && sid.startsWith("tmp-")) { continue; } // attendre la synchro de l'ouverture de caisse
          const url = op.type === "sale" ? "/sales"
            : op.type === "cash_close" ? `/cash/sessions/${sid}/close`
            : op.url;
          const payload = resolveVal(op.payload, map);
          if (hasTmp(payload)) { op.status = "error"; op.error = "Référence locale non résolue"; await queuePut(op); failed.add(op.uid); continue; }
          const { data } = await api({ method, url, data: payload });
          if (op.providesId && data && data.id) { map[op.providesId] = data.id; await kvSet("idMap", map); }
          op.status = "synced";
          op.synced_at = new Date().toISOString();
          op.error = null;
          await queuePut(op); done.add(op.uid);
        } catch (e) {
          const status = e?.response?.status;
          op.attempts = (op.attempts || 0) + 1;
          if (status && status >= 400 && status < 500 && status !== 429) {
            // permanent business error (e.g. stock discrepancy) — keep, flag, never delete
            op.status = "error";
            op.error = e?.response?.data?.detail || "Erreur serveur";
            failed.add(op.uid);  // block dependents in this same pass
          } else {
            // transient/network error — keep pending, retry later
            op.status = "pending";
            op.error = "Reprise en attente";
          }
          await queuePut(op);
        }
      }
      await reloadQueue();
    } finally {
      flushing.current = false;
      setSyncing(false);
    }
  }, [reloadQueue]);

  const offlineAction = useCallback(async (op) => {
    const uid = op.uid || (op.payload && op.payload.client_uid) || newUid();
    return enqueue({ ...op, uid });
  }, [enqueue]);

  const retryOp = useCallback(async (uid) => {
    const all = await queueAll();
    const op = all.find((o) => o.uid === uid);
    if (op) { op.status = "pending"; op.error = null; await queuePut(op); await reloadQueue(); flush().catch(() => {}); }
  }, [flush, reloadQueue]);

  const clearSynced = useCallback(async () => {
    const all = await queueAll();
    for (const op of all) if (op.status === "synced") await queueDel(op.uid);
    await reloadQueue();
  }, [reloadQueue]);

  const markCashClosed = useCallback(async () => {
    const b = (await kvGet("bootstrap")) || bootstrap;
    if (b) { const nb = { ...b, cash_session: null }; await kvSet("bootstrap", nb); setBootstrap(nb); }
  }, [bootstrap]);

  const markCashOpen = useCallback(async (session) => {
    const b = (await kvGet("bootstrap")) || bootstrap || {};
    const nb = { ...b, cash_session: session }; await kvSet("bootstrap", nb); setBootstrap(nb);
  }, [bootstrap]);

  useEffect(() => { if (online) flush(); }, [online, flush]);
  useEffect(() => {
    const id = setInterval(() => { if (navigator.onLine) flush(); }, 20000);
    return () => clearInterval(id);
  }, [flush]);

  const pendingCount = queue.filter((o) => o.status === "pending").length;
  const errorCount = queue.filter((o) => o.status === "error").length;

  const value = {
    online, bootstrap, lastSync, queue, syncing, deviceId,
    refreshBootstrap, enqueue, flush, retryOp, clearSynced, reloadQueue, markCashClosed, markCashOpen, offlineAction,
    pendingBaseQty, pendingCount, errorCount,
  };
  return <OfflineCtx.Provider value={value}>{children}</OfflineCtx.Provider>;
}
